<template>
    <TabsRoot
        v-if="items.length"
        v-model="index"
        as="section"
        :class="classes"
        :activation-mode="activationMode"
        aria-roledescription="carousel"
        :aria-label="label"
    >
        <div
            ref="stageRef"
            :class="element('stage')"
        >
            <!--
                One panel per image, and reka unmounts the inactive ones — so exactly one
                image is ever in the document, including in the server-rendered HTML, which
                is what keeps a single LCP candidate. The wrappers stay, which is the point:
                reka needs a registered panel per value to wire `aria-controls` on the tabs,
                and it labels each panel by its own thumbnail.
            -->
            <TabsContent
                v-for="(item, position) in items"
                :key="item.src + position"
                :value="position"
                aria-roledescription="slide"
                :class="element('panel')"
                @keydown="onNavigationKeydown"
            >
                <KFrame
                    :ratio="stageRatio"
                    :class="element('frame', modifier('zoomable', zoomable))"
                    @click="openZoom"
                >
                    <slot
                        name="media"
                        :item="item"
                        :index="position"
                    >
                        <img
                            :src="item.src"
                            :alt="item.alt ?? ''"
                        >
                    </slot>
                </KFrame>
            </TabsContent>

            <template v-if="showArrows">
                <button
                    type="button"
                    :class="element('control', modifier('prev'))"
                    :disabled="!loop && index === 0"
                    :aria-label="previousLabel"
                    @click="go(-1)"
                >
                    <span aria-hidden="true">
                        <slot name="prev-icon">
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            >
                                <path d="m15 18-6-6 6-6" />
                            </svg>
                        </slot>
                    </span>
                </button>

                <button
                    type="button"
                    :class="element('control', modifier('next'))"
                    :disabled="!loop && index === items.length - 1"
                    :aria-label="nextLabel"
                    @click="go(1)"
                >
                    <span aria-hidden="true">
                        <slot name="next-icon">
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            >
                                <path d="m9 18 6-6-6-6" />
                            </svg>
                        </slot>
                    </span>
                </button>
            </template>

            <!-- The rail already carries position for assistive tech and there is no
                autoplay to announce, so this is decoration. -->
            <p
                v-if="showCounter"
                :class="element('counter')"
                aria-hidden="true"
            >
                {{ index + 1 }} / {{ items.length }}
            </p>

            <KLightbox
                v-if="zoomable"
                v-model:open="zoomOpen"
                v-model:index="index"
                :items="items"
                :label="label"
                :loop="loop"
                :counter="counter"
            >
                <!--
                    A real DialogTrigger, not a plain button: reka takes the element to
                    restore focus to from whatever is focused when the dialog mounts, and
                    skips it when that is the body — which is exactly what clicking the
                    photo would otherwise leave behind.
                -->
                <template #trigger>
                    <DialogTrigger
                        ref="expandRef"
                        :class="element('control', modifier('expand'))"
                        :aria-label="expandLabel"
                    >
                        <span aria-hidden="true">
                            <slot name="expand-icon">
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                >
                                    <path d="M8 3H5a2 2 0 0 0-2 2v3m13-5h3a2 2 0 0 1 2 2v3M16 21h3a2 2 0 0 0 2-2v-3M8 21H5a2 2 0 0 1-2-2v-3" />
                                </svg>
                            </slot>
                        </span>
                    </DialogTrigger>
                </template>

                <!--
                    Only forwarded when filled. Left alone, KLightbox falls through to its
                    own full-size `<img>`, which is a better default than reusing whatever
                    stage-sized image the `media` slot renders.
                -->
                <template
                    v-if="$slots.fullscreen"
                    #media="mediaProps"
                >
                    <slot
                        name="fullscreen"
                        v-bind="mediaProps"
                    />
                </template>

                <template
                    v-if="$slots['close-icon']"
                    #close-icon
                >
                    <slot name="close-icon" />
                </template>

                <template
                    v-if="$slots['prev-icon']"
                    #prev-icon
                >
                    <slot name="prev-icon" />
                </template>

                <template
                    v-if="$slots['next-icon']"
                    #next-icon
                >
                    <slot name="next-icon" />
                </template>
            </KLightbox>
        </div>

        <TabsList
            v-if="showRail"
            :loop="loop"
            :class="element('rail')"
        >
            <TabsTrigger
                v-for="(item, position) in items"
                :key="item.src + position"
                ref="thumbRefs"
                :value="position"
                :aria-label="slideLabel(position)"
                :class="element('thumb')"
            >
                <KFrame
                    as="span"
                    :ratio="thumbRatio"
                    :class="element('thumb-frame')"
                >
                    <slot
                        name="thumbnail"
                        :item="item"
                        :index="position"
                        :active="position === index"
                    >
                        <img
                            :src="item.thumbSrc ?? item.src"
                            alt=""
                        >
                    </slot>
                </KFrame>
            </TabsTrigger>
        </TabsList>
    </TabsRoot>
</template>

<script setup lang="ts">
import type { KMediaItem } from '../../types'
import type { KGalleryProps } from './types'
import { unrefElement, useSwipe } from '@vueuse/core'
import useBem from '@yeeroen/use-bem'
import { DialogTrigger, TabsContent, TabsList, TabsRoot, TabsTrigger } from 'reka-ui'
import { computed, ref, useTemplateRef, watch } from 'vue'
import KFrame from '../frame/k-frame.vue'
import KLightbox from '../lightbox/k-lightbox.vue'

defineOptions({ name: 'KGallery' })

const props = withDefaults(defineProps<KGalleryProps>(), {
    items: () => [],
    label: 'Gallery',
    arrows: true,
    counter: true,
    loop: true,
    zoomable: true,
    activationMode: 'manual',
    expandLabel: 'View full screen',
    previousLabel: 'Previous slide',
    nextLabel: 'Next slide',
})

const emit = defineEmits<{
    /** The viewer was opened, at this index. Its open state is `v-model` on KLightbox. */
    expand: [index: number]
}>()

defineSlots<{
    /** The stage image. Falls back to a plain `<img>` built from `item.src`. */
    'media'?: (props: { item: KMediaItem, index: number }) => unknown
    /** One rail thumbnail. Falls back to `item.thumbSrc ?? item.src`. */
    'thumbnail'?: (props: { item: KMediaItem, index: number, active: boolean }) => unknown
    /** The image inside the viewer. Unfilled, KLightbox renders its own full-size `<img>`. */
    'fullscreen'?: (props: { item: KMediaItem, index: number }) => unknown
    'prev-icon'?: () => unknown
    'next-icon'?: () => unknown
    'expand-icon'?: () => unknown
    'close-icon'?: () => unknown
}>()

const index = defineModel<number>('index', { default: 0 })

const zoomOpen = ref(false)

// A swipe on a touch screen ends in a click, which would open the viewer at the end of
// every drag. Cleared on a timer because the click arrives after the gesture, not with it.
const swiped = ref(false)

const stageRef = useTemplateRef<HTMLElement>('stageRef')
const expandRef = useTemplateRef('expandRef')
const thumbRefs = useTemplateRef<{ $el: HTMLElement }[]>('thumbRefs')

const { block, element, modifier } = useBem('k-gallery')

const showRail = computed(() => props.items.length > 1)
const showArrows = computed(() => props.arrows && props.items.length > 1)
const showCounter = computed(() => props.counter && props.items.length > 1)

// Read through a custom property rather than taken as a prop: the ratio a gallery wants is
// usually per breakpoint (wide in a desktop column, squarer on a phone), and a prop cannot
// say that.
const stageRatio = 'var(--gallery-ratio, 16 / 9)'
const thumbRatio = 'var(--gallery-thumb-ratio, 4 / 3)'

const classes = computed(() => block(modifier('zoomable', props.zoomable)))

const slideLabel = (position: number) => `Slide ${position + 1} of ${props.items.length}`

function go(step: number) {
    const total = props.items.length
    if (total < 2)
        return

    const next = index.value + step
    index.value = props.loop
        ? (next + total) % total
        : Math.min(Math.max(next, 0), total - 1)
}

function onNavigationKeydown(event: KeyboardEvent) {
    if (event.key === 'ArrowLeft')
        go(-1)
    else if (event.key === 'ArrowRight')
        go(1)
}

function openZoom() {
    if (!props.zoomable || swiped.value)
        return

    // Focused first so reka has something to hand focus back to when the viewer closes.
    const trigger = unrefElement(expandRef) as HTMLElement | undefined
    trigger?.focus()

    zoomOpen.value = true
    emit('expand', index.value)
}

// Bound to the stage rather than the frame: the frame is inside a panel reka swaps out on
// every move, so a listener there would be torn off mid-gesture.
useSwipe(stageRef, {
    threshold: 40,
    onSwipeEnd(_event, direction) {
        if (direction !== 'left' && direction !== 'right')
            return

        swiped.value = true
        setTimeout(() => {
            swiped.value = false
        }, 400)

        go(direction === 'left' ? 1 : -1)
    },
})

// Keep the selected thumbnail in view. `inline: 'nearest'` is a no-op when it already is,
// and the scroll stays instant on purpose — reka's roving focus scrolls the rail itself on
// every arrow key, and a smooth scroll here would fight it.
watch(index, (position) => {
    thumbRefs.value?.[position]?.$el?.scrollIntoView({ inline: 'nearest', block: 'nearest' })
})

// A shrinking list (a CMS preview editing the gallery live) would otherwise leave the index
// past the end and blank the stage.
watch(() => props.items.length, (total) => {
    if (total > 0 && index.value > total - 1)
        index.value = total - 1
})
</script>

<style lang="scss">
@use './k-gallery.scss';
</style>
